import React, { useMemo } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useTranslation } from 'react-i18next';
import { ReactQueryProvider } from '@/providers/react-query-provider';
import { Tabs } from '@/components/design-system/molecules/tabs';
import { ToastProvider } from '@/providers/toast-provider';
import {
  AdminRoutes,
  AdminSideNavigation,
} from '@/features/admin/navigation/containers/admin-side-navigation';
import { RentsList } from '@/features/admin/rents/components/rents-list';
import { RentsSummary } from '@/features/admin/rents/components/rents-summary';
import { RentReportsList } from '@/features/admin/rents/components/report-list';
import { DiscussionThreadsProvider } from '@/features/shared/discussion-threads/providers';
import { AuthContextProvider } from '@/hooks/use-auth';
import { NotificationsProvider } from '@/features/shared/notifications/provider';
import { PopUpConfirmationProvider } from '@/features/shared/pop-up-confirmation/provider';
import { BrowserRouter, useSearchParams } from 'react-router-dom';

const TAB_NAMES = ['summary', 'all', 'reports'];

const RentsScreenContent = ({ ...props }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTab = useMemo(() => {
    const tabParam = searchParams.get('tab') ?? 'all';
    const tabIndex = TAB_NAMES.indexOf(tabParam);
    return tabIndex !== -1 ? tabIndex : 1;
  }, [searchParams]);

  const handleTabChange = (index: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', TAB_NAMES[Number(index)] ?? 'all');
    setSearchParams(newParams);
  };

  return (
    <>
          {/* Dev tools will only be displayed during development */}
          <ReactQueryDevtools initialIsOpen={false} />
          <div className="flex h-screen w-screen">
            <AdminSideNavigation tabToSet={AdminRoutes.Rents} />
            <div className="flex h-full flex-1 flex-col overflow-hidden px-7 pt-7">
              <h1 className="pb-3 text-h3 font-bold text-text-primary">
                {t('js.features.admin.rents.title')}
              </h1>
              <div className="flex w-full flex-1 flex-col overflow-hidden bg-E10">
                <Tabs
                  selectedIndex={selectedTab}
                  onChange={(index) => handleTabChange(Number(index))}
                >
                  <Tabs.TabsList>
                    <Tabs.Tab>
                      <Tabs.TabDefaultContent>
                        {t('js.features.admin.rents.rent_page_tabs.summary') ??
                          ''}
                      </Tabs.TabDefaultContent>
                    </Tabs.Tab>
                    <Tabs.Tab>
                      <Tabs.TabDefaultContent>
                        {t('js.features.admin.rents.rent_page_tabs.all') ?? ''}
                      </Tabs.TabDefaultContent>
                    </Tabs.Tab>
                    <Tabs.Tab>
                      <Tabs.TabDefaultContent>
                        {t('js.features.admin.rents.rent_page_tabs.reports') ??
                          ''}
                      </Tabs.TabDefaultContent>
                    </Tabs.Tab>
                  </Tabs.TabsList>
                  <Tabs.TabsScreens>
                    <Tabs.TabScreen>
                      <RentsSummary />
                    </Tabs.TabScreen>
                    <Tabs.TabScreen>
                      {props.contract_id &&
                        <div className="text-lead2 text-text-tertiary my-2">
                          Filtered by contract #{props.contract_id}
                        </div>
                      }
                      <RentsList />
                    </Tabs.TabScreen>
                    <Tabs.TabScreen className="relative">
                      <RentReportsList />
                    </Tabs.TabScreen>
                  </Tabs.TabsScreens>
                </Tabs>
              </div>
            </div>
          </div>
    </>
  );
};

const RentsScreen = ({ ...props }) => {
  return (
    <ReactQueryProvider>
      <ToastProvider />
      <BrowserRouter>
        <AuthContextProvider>
          <DiscussionThreadsProvider>
            <NotificationsProvider>
              <PopUpConfirmationProvider>
                <RentsScreenContent {...props} />
              </PopUpConfirmationProvider>
            </NotificationsProvider>
          </DiscussionThreadsProvider>
        </AuthContextProvider>
      </BrowserRouter>
    </ReactQueryProvider>
  );
};

export { RentsScreen };
